import {Button, Dropdown, Flex, MenuProps, Modal, Space} from "antd";
import React, {Fragment, useEffect} from "react";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {
    faBolt,
    faBoxArchive,
    faChildren,
    faMap,
    faMessage,
    faPlus,
    faRightLeft
} from "@fortawesome/free-solid-svg-icons";
import {useAppDispatch, useAppSelector} from "../../../feature/tools";
import LayersList from "../../common/LayerList";
import {
    Character,
    Object,
    Location,
    Shot,
    Transformable,
    Narration,
    Action
} from "../../../feature/project/Shot";
import {AnimationType} from "../../common/Animations";
import {Description} from "../../../typography/Description";
import {deleteShotTransition, setShotTransition} from "../../../feature/project/shotSlice";
import {useParams} from "react-router-dom";
import {SelectAnimationModal} from "../../common/SelectAnimationModal";
import {LocationEditModal} from "./LocationEditModal";
import {deleteLocation, saveLocation} from "../../../feature/project/locationsSlice";
import {deleteObject, saveObject} from "../../../feature/project/objectsSlice";
import {ObjectEditModal} from "./ObjectEditModal";
import {deleteCharacter, saveCharacter} from "../../../feature/project/charactersSlice";
import {CharacterEditModal} from "./CharacterEditModal";
import {NarrationEditModal} from "./NarrationEditModal";
import {deleteNarration, saveNarration} from "../../../feature/project/narrationSlice";
import {ActionEditDialog} from "./ActionEditDialog";
import {deleteAction, saveShotAction} from "../../../feature/project/actionSlice";
import {editorRoles, hasRoles, WithUserPermission} from "../../common/WithUserPermission";

const {confirm} = Modal;

export const ShotLayers = (
    props: {
        selectedElement: Transformable | undefined,
        onElementSelected: (element: Transformable | undefined) => void,
        onLocationSelected: () => void,
        locationSelected: boolean
    }
) => {
    const shot = useAppSelector(state => state.shots.shot)
    const {projectId} = useParams()
    const dispatch = useAppDispatch()
    const [showTransitions, setShowTransitions] = React.useState<AnimationType | undefined>(undefined)
    const [showLocation, setShowLocation] = React.useState<boolean>(false)
    const [showObject, setShowObject] = React.useState<Object | undefined>(undefined)
    const [showCharacter, setShowCharacter] = React.useState<Character | undefined>(undefined)
    const [showNarration, setShowNarration] = React.useState<Narration | undefined>(undefined)
    const [showEditActionDialog, setShowEditActionDialog] = React.useState<Action | undefined>(undefined)
    const user = useAppSelector(state => state.users.currentUser)
    useEffect(() => {
        setShowObject(shot?.objects?.find(object => object.id === showObject?.id))
        setShowCharacter(shot?.characters?.find(character => character.id === showCharacter?.id))
        setShowEditActionDialog(shot?.actions?.find(action => action.id === showEditActionDialog?.id))
        setShowNarration(showNarration ? shot?.narration : showNarration)
    }, [shot])

    const transitions = [
        {
            key: AnimationType.TransitionIn,
            label: 'Transition In'
        }, {
            key: AnimationType.TransitionOut,
            label: 'Transition Out'
        }].filter(item => (item.key === AnimationType.TransitionIn && !shot?.transitionIn) || (item.key === AnimationType.TransitionOut && !shot?.transitionOut))

    const onTransitionClick: MenuProps['onClick'] = (e) => {
        setShowTransitions(e.key as AnimationType)
    }

    const showConfirmLocationDeletion = (item: Location) => {
        confirm({
            title: 'Delete Location',
            content: 'Are you sure you want to delete this location? It will also delete all the layers associated with it.',
            icon: <></>,
            okText: 'Yes, delete',
            okButtonProps: {danger: true, type: 'primary'},
            cancelText: 'No',
            onOk() {
                if (projectId && item.id) {
                    dispatch(deleteLocation({projectId: parseInt(projectId), locationId: item.id}))
                }
            },
        });
    }

    const showConfirmObjectDeletion = (item: Object) => {
        confirm({
            title: 'Delete Object',
            content: 'Are you sure you want to delete this object? It will also delete all the layers associated with it.',
            icon: <></>,
            okText: 'Yes, delete',
            okButtonProps: {danger: true, type: 'primary'},
            cancelText: 'No',
            onOk() {
                if (projectId && item.id) {
                    dispatch(deleteObject({projectId: parseInt(projectId), objectId: item.id}))
                }
            },
        });
    }
    const showConfirmNarrationDeletion = (item: Narration) => {
        confirm({
            title: 'Delete Narration',
            content: 'Are you sure you want to delete this narration?',
            icon: <></>,
            okText: 'Yes, delete',
            okButtonProps: {danger: true, type: 'primary'},
            cancelText: 'No',
            onOk() {
                if (projectId && item.id) {
                    dispatch(deleteNarration({projectId: parseInt(projectId), narrationId: item.id}))
                }
            },
        });
    }

    const showConfirmActionDeletion = (item: Action) => {
        confirm({
            title: 'Delete Action',
            content: 'Are you sure you want to delete this action?',
            icon: <></>,
            okText: 'Yes, delete',
            okButtonProps: {danger: true, type: 'primary'},
            cancelText: 'No',
            onOk() {
                if (projectId && item.id) {
                    dispatch(deleteAction({projectId: parseInt(projectId), actionId: item.id}))
                }
            },
        });
    }

    const showConfirmCharacterDeletion = (item: Character) => {
        confirm({
            title: 'Delete Character',
            content: 'Are you sure you want to delete this character? It will also delete all the layers associated with it.',
            icon: <></>,
            okText: 'Yes, delete',
            okButtonProps: {danger: true, type: 'primary'},
            cancelText: 'No',
            onOk() {
                if (projectId && item.id) {
                    dispatch(deleteCharacter({projectId: parseInt(projectId), characterId: item.id}))
                }
            },
        });
    }

    const transitionsMenu = {
        items: transitions,
        onClick: onTransitionClick,
    };

    return (
        <Fragment>
            <div style={{
                overflowY: 'auto', scrollbarWidth: "none",
                overflowX: 'visible',
                height: 'calc(100% - 30px)',
                width: '100%'
            }}>
                <div>
                    <Flex justify={'space-between'} className={'layer-group-title'}
                          align={'center'}>
                        <Space style={{height: 32}}><FontAwesomeIcon icon={faMap}/>Location</Space>

                        <WithUserPermission roles={editorRoles}>
                            <Button
                                disabled={!!shot?.location}
                                icon={<FontAwesomeIcon icon={faPlus}/>}
                                type={'text'}
                                onClick={() => setShowLocation(true)}
                            />
                        </WithUserPermission>
                    </Flex>
                    {shot?.location ? <LayersList
                            clickable={hasRoles(user, editorRoles)}
                            is={(item) => props.locationSelected}
                            data={shot.location}
                            title={(item) => item.title}
                            onItemClick={(item) => props.onLocationSelected()}
                            onDeleteClick={(item) => showConfirmLocationDeletion(item)}
                            onEditClick={(item) => setShowLocation(true)}
                        /> :
                        <Flex align={'center'} justify={'center'} style={{padding: 20}}>No location added</Flex>}
                </div>
                <div>
                    <Flex justify={'space-between'} className={'layer-group-title'}
                          align={'center'}><Space style={{height: 32}}><FontAwesomeIcon
                        icon={faChildren}/>Characters</Space>
                        <WithUserPermission roles={editorRoles}>
                            <Button
                                icon={<FontAwesomeIcon icon={faPlus}/>}
                                type={'text'}
                                onClick={() => setShowCharacter({})}/>
                        </WithUserPermission>
                    </Flex>
                    {shot?.characters && shot.characters.length > 0 ?
                        <LayersList

                            clickable={hasRoles(user, editorRoles)}
                            is={(item) => props.selectedElement?.type === 'Character' && item.id === props.selectedElement?.id}
                            data={shot.characters.map(character => {
                                return {
                                    ...character,
                                    type: 'Character'
                                } as Character
                            })}
                            title={(item) => item.title}
                            onItemClick={(item) => props.onElementSelected(item)}
                            onDeleteClick={(item) => showConfirmCharacterDeletion(item)}
                            onEditClick={(item) => setShowCharacter(item)}
                        /> :
                        <Flex align={'center'} justify={'center'} style={{padding: 20}}>No characters added</Flex>}
                </div>
                <div>
                    <Flex justify={'space-between'} className={'layer-group-title'}
                          align={'center'}>
                        <Space style={{height: 32}}>
                            <FontAwesomeIcon icon={faBoxArchive}/>Objects</Space>
                        <WithUserPermission roles={editorRoles}>
                            <Button
                                icon={<FontAwesomeIcon icon={faPlus}/>}
                                type={'text'}
                                onClick={() => setShowObject({})}
                            />
                        </WithUserPermission>
                    </Flex>
                    {shot?.objects && shot.objects.length ?
                        <LayersList

                            clickable={hasRoles(user, editorRoles)}
                            is={(item) => props.selectedElement?.type === 'Object' && item.id === props.selectedElement?.id}
                            data={shot.objects.map(object => {
                                return {
                                    ...object,
                                    type: 'Object'
                                } as Object
                            })}
                            title={(item) => item.title}
                            onItemClick={(item) => props.onElementSelected(item)}
                            onDeleteClick={(item) => showConfirmObjectDeletion(item)}
                            onEditClick={(item) => setShowObject(item)}
                        /> :
                        <Flex align={'center'} justify={'center'} style={{padding: 20}}>No objects added</Flex>}
                </div>
                <div>
                    <Flex justify={'space-between'} className={'layer-group-title'}
                          align={'center'}>
                        <Space style={{height: 32}}><FontAwesomeIcon icon={faMessage}/>Narration</Space>
                        <WithUserPermission roles={editorRoles}>
                            <Button
                                icon={<FontAwesomeIcon icon={faPlus}/>}
                                type={'text'}
                                disabled={!!shot?.narration}
                                onClick={() => setShowNarration({} as Narration)}
                            />
                        </WithUserPermission>
                    </Flex>
                    {shot?.narration ? <LayersList

                            clickable={hasRoles(user, editorRoles)}
                            data={{...shot.narration, type: 'Narrative'} as Narration}
                            title={(item) => item.text}
                            onItemClick={(item) => props.onElementSelected(item)}
                            onDeleteClick={(item) => showConfirmNarrationDeletion(item)}
                            onEditClick={(item) => setShowNarration(item)}
                            is={(item) => props.selectedElement?.type === 'Narrative' && item.id === props.selectedElement?.id}
                        /> :
                        <Flex align={'center'} justify={'center'} style={{padding: 20}}>No narration added</Flex>}
                </div>
                <div>
                    <Flex justify={'space-between'} className={'layer-group-title'}
                          align={'center'}>
                        <Space style={{height: 32}}><FontAwesomeIcon icon={faBolt}/>Actions</Space>
                        <WithUserPermission roles={editorRoles}>
                            <Button
                                icon={<FontAwesomeIcon icon={faPlus}/>}
                                type={'text'}
                                onClick={() => setShowEditActionDialog({title: "", arguments: []} as Action)}
                            />
                        </WithUserPermission>
                    </Flex>
                    {shot?.actions ? <LayersList

                            clickable={hasRoles(user, editorRoles)}
                            data={shot.actions.map(action => {
                                return {
                                    ...action,
                                    type: 'Action'
                                } as Action
                            })}
                            title={(item) => item.title}
                            onItemClick={(item) => {
                                //TODO
                            }}
                            onDeleteClick={(item) => showConfirmActionDeletion(item)}
                            onEditClick={(item) => setShowEditActionDialog(item)}
                            is={(item) => props.selectedElement?.type === 'Action' && item.id === props.selectedElement?.id}
                        /> :
                        <Flex align={'center'} justify={'center'} style={{padding: 20}}>No actions added</Flex>}
                </div>
                <div>
                    <Flex justify={'space-between'} className={'layer-group-title'}
                          align={'center'}><Space style={{height: 32}}><FontAwesomeIcon icon={faRightLeft}/>Transitions</Space>
                        <WithUserPermission roles={editorRoles}><Dropdown
                            disabled={transitions.length === 0} menu={transitionsMenu} trigger={["click"]}>
                            <Button
                                icon={<FontAwesomeIcon icon={faPlus}/>} type={'text'}
                            />
                        </Dropdown> </WithUserPermission></Flex>
                    {shot?.transitionIn || shot?.transitionOut ? <LayersList

                            clickable={hasRoles(user, editorRoles)}
                            data={[{
                                type: AnimationType.TransitionIn,
                                animation: shot.transitionIn
                            }, {
                                type: AnimationType.TransitionOut,
                                animation: shot.transitionOut
                            }].filter(item => item.animation !== null && item.animation !== undefined)}
                            title={(item) => {
                                return (<Flex justify={'space-between'} align={'center'}>
                                    <div>{item?.animation?.title}</div>
                                    <Description value={item?.type}/>
                                </Flex>)
                            }}
                            onItemClick={(item) => setShowTransitions(item.type)}
                            onDeleteClick={(item) => {
                                if (projectId && shot.id) {
                                    dispatch(deleteShotTransition({
                                        projectId: parseInt(projectId),
                                        shotId: shot.id,
                                        type: item.type
                                    }))
                                }
                            }}
                            onEditClick={(item) => setShowTransitions(item.type)}
                        /> :
                        <Flex align={'center'} justify={'center'} style={{padding: 20}}>No transition added</Flex>}
                </div>
            </div>
            {showTransitions ?
                <SelectAnimationModal
                    onSelect={(animation, type) => {
                        if (projectId && shot?.id) {
                            dispatch(setShotTransition({
                                projectId: parseInt(projectId),
                                shotId: shot.id,
                                type: type,
                                transition: animation
                            }))
                        }
                    }}
                    onCancel={() => setShowTransitions(undefined)}
                    type={showTransitions}
                /> : ''}

            {showLocation ?
                <LocationEditModal
                    location={shot?.location || {}}
                    onCancel={() => setShowLocation(false)}
                    onSave={(location) => {
                        if (projectId && location && shot) {
                            dispatch(saveLocation({
                                projectId: parseInt(projectId),
                                location: {...location, shot: {id: shot.id} as Shot}
                            }))
                        }
                        setShowLocation(false)
                    }}
                /> : ''}
            {showObject ?
                <ObjectEditModal
                    object={showObject || {}}
                    onCancel={() => setShowObject(undefined)}
                    onSave={(object) => {
                        if (projectId && object && shot) {
                            dispatch(saveObject({
                                projectId: parseInt(projectId),
                                object: {...object, shot: {id: shot.id} as Shot}
                            }))
                        }
                        setShowObject(undefined)
                    }}
                /> : ''}
            {showCharacter ?
                <CharacterEditModal
                    character={showCharacter || {}}
                    onCancel={() => setShowCharacter(undefined)}
                    onSave={(character) => {
                        if (projectId && character && shot) {
                            dispatch(saveCharacter({
                                projectId: parseInt(projectId),
                                character: {...character, shot: {id: shot.id} as Shot}
                            }))
                        }
                        setShowCharacter(undefined)
                    }}
                /> : ''}
            {showNarration ?
                <NarrationEditModal
                    narration={showNarration || {}}
                    onCancel={() => setShowNarration(undefined)}
                    onSave={(narration) => {
                        if (projectId && narration && shot?.id) {
                            dispatch(saveNarration({
                                projectId: parseInt(projectId),
                                shotId: shot.id,
                                narration: {...narration}
                            }))
                        }
                        setShowNarration(undefined)
                    }}
                />
                : ''}
            {showEditActionDialog ?
                <ActionEditDialog
                    action={showEditActionDialog}
                    onSave={(action: Action) => {
                        if (projectId && shot?.id) {
                            dispatch(saveShotAction({
                                projectId: parseInt(projectId),
                                shotId: shot.id,
                                action: action
                            }))
                        }
                        setShowEditActionDialog(undefined)
                    }}
                    onCancel={() => setShowEditActionDialog(undefined)}
                />
                : ''}
        </Fragment>
    )
}